if (!customElements.get('sender-cart-pack-picker')) {
  let switching = false;

  const propertiesOf = item => Object.fromEntries(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  const signature = (item, variant = item.variant_id || item.id) => JSON.stringify([String(variant), item.selling_plan_allocation?.selling_plan?.id || null, propertiesOf(item)]);
  const matching = (cart, identity) => cart.items.filter(item => !item.parent_relationship?.parent && signature(item) === identity);
  const sole = (cart, identity) => {
    const items = matching(cart, identity);
    if (items.length > 1) throw new Error('Ambiguous cart position');
    return items[0];
  };
  const endpoint = name => {
    const root = window.Shopify?.routes?.root || '/';
    const route = window.routes?.['cart_' + name + '_url'] || root + 'cart/' + name;
    return route.endsWith('.js') ? route : route + '.js';
  };
  const readCart = () => request((window.routes?.cart_url || (window.Shopify?.routes?.root || '/') + 'cart') + '.js');
  async function request(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
        ...(body ? {body: JSON.stringify(body)} : {})
      });
      const data = await response.json();
      if (!response.ok || data.errors || data.status >= 400) {
        const error = new Error('Cart request rejected');
        error.definitive = response.status >= 400 && response.status < 500;
        throw error;
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  const sections = () => ({sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname});
  const change = (key, quantity) => request(endpoint('change'), {id: key, quantity, ...sections()});

  async function paint(cart, drawer, variant, message, isError) {
    let rendered = cart.sections;
    if (!rendered?.['cart-drawer'] || !rendered?.['cart-icon-bubble']) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      rendered = await request(url.pathname + url.search);
    }
    const parser = new DOMParser();
    const updated = parser.parseFromString(rendered['cart-drawer'] || '', 'text/html').querySelector('#CartDrawer');
    if (!updated) throw new Error('Cart rendering unavailable');
    const scroller = drawer.querySelector('cart-drawer-items');
    const scrollTop = scroller?.scrollTop || 0;
    drawer.querySelector('#CartDrawer').innerHTML = updated.innerHTML;
    drawer.classList.toggle('is-empty', cart.item_count === 0);
    const bubble = document.getElementById('cart-icon-bubble');
    const nextBubble = parser.parseFromString(rendered['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
    if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    const note = drawer.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) drawer.setSummaryAccessibility?.(note);
    const selected = [...drawer.querySelectorAll('[data-pack-variant]')].find(button => button.dataset.packVariant === String(variant) && button.getAttribute('aria-pressed') === 'true');
    const picker = selected?.closest('sender-cart-pack-picker') || drawer.querySelector('sender-cart-pack-picker');
    if (picker) picker.message(message, isError);
    else {
      const errorBox = drawer.querySelector('#CartDrawer-CartErrors');
      if (errorBox) errorBox.textContent = message;
    }
    if (drawer.querySelector('cart-drawer-items')) drawer.querySelector('cart-drawer-items').scrollTop = scrollTop;
    if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
      const focus = selected || drawer.querySelector('.drawer__close');
      trapFocus(drawer.querySelector('.drawer__inner'), focus);
      focus?.focus({preventScroll: true});
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items', cartData: cart, variantId: variant});
    }
  }

  customElements.define('sender-cart-pack-picker', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-pack-variant]');
        if (!button || button.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        if (button.getAttribute('aria-pressed') !== 'true') this.choose(button);
      }, {signal: this.controller.signal});
    }

    disconnectedCallback() { this.controller?.abort(); }

    message(text, isError = false) {
      const status = this.querySelector('.sender-cart-packs__status');
      status.textContent = text;
      status.hidden = !text;
      status.classList.toggle('is-error', isError);
      this.querySelector('.sender-cart-packs__check').hidden = !isError;
    }

    async choose(button) {
      const drawer = this.closest('cart-drawer');
      if (switching || !drawer || drawer.querySelector('.cart__items--disabled') || drawer.querySelector('[data-sender-upsell-add]:disabled')) return;
      if ([...drawer.querySelectorAll('.quantity__input')].some(input => input.value !== input.defaultValue)) return;
      switching = true;
      const disabled = [...drawer.querySelectorAll('button:not(.drawer__close), input, textarea, select')].map(node => [node, node.disabled]);
      disabled.forEach(([node]) => { node.disabled = true; });
      drawer.setAttribute('aria-busy', 'true');
      const guard = new AbortController();
      ['click', 'change', 'submit'].forEach(type => drawer.addEventListener(type, event => {
        if (event.target.closest('.drawer__close, #CartDrawer-Overlay')) return;
        if (event.target.closest('button,input,textarea,select,form,a')) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, {capture: true, signal: guard.signal}));
      const targetId = button.dataset.packVariant;
      let source, sourceIdentity, targetIdentity, previousTarget = 0, cart, attempted = false;
      let success = false, message = this.dataset.error;
      this.message(this.dataset.working);
      try {
        cart = await readCart();
        source = cart.items.find(item => item.key === this.dataset.lineKey);
        if (!source || source.quantity !== Number(this.dataset.quantity) || source.selling_plan_allocation || source.parent_relationship?.parent) {
          message = this.dataset.stale;
          throw new Error('Stale cart position');
        }
        sourceIdentity = signature(source);
        targetIdentity = signature(source, targetId);
        if (String(source.variant_id || source.id) === targetId || matching(cart, sourceIdentity).length !== 1) throw new Error('Unsupported cart position');
        previousTarget = sole(cart, targetIdentity)?.quantity || 0;
        let addError;
        attempted = true;
        try {
          await request(endpoint('add'), {items: [{id: targetId, quantity: source.quantity, properties: propertiesOf(source)}]});
        } catch (error) { addError = error; }
        cart = await readCart();
        let oldLine = sole(cart, sourceIdentity);
        let newLine = sole(cart, targetIdentity);
        if (!addError?.definitive && oldLine?.quantity === source.quantity && newLine?.quantity > previousTarget + source.quantity) {
          cart = await change(newLine.key, previousTarget + source.quantity);
          oldLine = sole(cart, sourceIdentity);
          newLine = sole(cart, targetIdentity);
        }
        if (addError?.definitive || !newLine || newLine.quantity !== previousTarget + source.quantity || oldLine?.quantity !== source.quantity) throw new Error('Package not added completely');
        cart = await change(oldLine.key, 0);
        success = !sole(cart, sourceIdentity) && sole(cart, targetIdentity)?.quantity === previousTarget + source.quantity;
        if (!success) throw new Error('Package swap incomplete');
      } catch (error) {
        try {
          cart = await readCart();
          if (attempted) {
            const oldLine = sole(cart, sourceIdentity);
            const newLine = sole(cart, targetIdentity);
            success = !oldLine && newLine?.quantity === previousTarget + source.quantity;
            if (!success && oldLine?.quantity === source.quantity) {
              const added = (newLine?.quantity || 0) - previousTarget;
              if (added > 0 && added <= source.quantity) cart = await change(newLine.key, previousTarget);
              if ((sole(cart, targetIdentity)?.quantity || 0) === previousTarget) message = this.dataset.restored;
            }
          }
        } catch (recoveryError) { message = this.dataset.error; }
      }
      try {
        if (!cart) throw new Error('Cart unavailable');
        await paint(cart, drawer, success ? targetId : source?.variant_id || source?.id, success ? this.dataset.success : message, !success);
      } catch (renderError) {
        this.message(this.dataset.error, true);
      } finally {
        disabled.forEach(([node, wasDisabled]) => { if (node.isConnected) node.disabled = wasDisabled; });
        drawer.removeAttribute('aria-busy');
        guard.abort();
        switching = false;
      }
    }
  });
}
